import { ChevronDownIcon } from '@chakra-ui/icons'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Td,
  Text,
  Tooltip,
  Tr,
  useBoolean,
  useDisclosure
} from '@chakra-ui/react'
import type { InsuranceParamsPoleasGridItem, InsuranceParamsVehicleGridItem, PersonAdditionalData } from 'BrokerTsLib/src/dtos'
import { formatPersonFullName, ImportInsuranceStates } from 'BrokerTsLib/src/dtos'
import type { IOfferCompany } from 'BrokerTsLib/src/enums'
import { Selection } from 'BrokerTsLib/src/enums'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { useCallback, useContext } from 'react'
import { Link } from 'react-router-dom'

import { VehicleLogo } from '@/components/VehicleLogo'
import { useAuth } from '@/context/AuthContext'
import { useGetInsuranceParamsNavigationTarget } from '@/hooks/useInsuranceParams'
import { poleasContext } from '@/pages/poleas/PoleasImportsPage'

import { CompanyLogo } from '../../components/CompanyLogo'
import { TableData } from '../../components/Grid'
import { StepsProgress } from '../../components/StepsProgress'
import { EditInsuranceParamsModal } from './EditInsuranceParamsModal'

export const logoSize = 44

interface ImportRowProps {
  allowSelect: boolean
  selectedRows: InsuranceParamsVehicleGridItem['insuranceParamsId'][]
  onRowSelect: (id: InsuranceParamsVehicleGridItem['insuranceParamsId']) => void
  onDeleteRow: (id: InsuranceParamsVehicleGridItem['insuranceParamsId']) => void
  onSendAccess: (item: InsuranceParamsVehicleGridItem) => void
  onDisplayPublicUrl: (item: InsuranceParamsPoleasGridItem) => void
  onToggleClientDisabled: (item: InsuranceParamsPoleasGridItem) => void
}

function formatInsuree(person?: PersonAdditionalData) {
  if (!person)
    return ''

  let result = formatPersonFullName(person)

  switch (person.type) {
    case Selection.Person:
      result = result + ' ' + person.birthNumber
      break
    case Selection.Company:
      result = result + ' ' + person.idendificationNumber
      break
    case Selection.SelfEmployed:
      result = result + ` ${person.idendificationNumber} (${person.birthNumber})`
      break
  }

  return result
}

export const ImportRow = ({ allowSelect, onRowSelect, onSendAccess, onToggleClientDisabled, onDisplayPublicUrl, onDeleteRow, selectedRows, item }: { item: InsuranceParamsPoleasGridItem } & ImportRowProps) => {
  const { isOpen: isOpenModal, onOpen: onOpenModal, onClose: onCloseModal } = useDisclosure()
  const { reload } = useContext(poleasContext)
  const auth = useAuth()

  const { insuranceParamsId: id } = item
  const isSelected = selectedRows.includes(id)
  const vehicleItem = item as InsuranceParamsPoleasGridItem

  const [buttonsVisible, setButtonsVisible] = useBoolean(false)

  const showEditModal = item.state != ImportInsuranceStates.Contracted && item.state != ImportInsuranceStates.ContractedByClient && !!auth.user?.poleas?.enableEdit
  const showDeleteButton = item.state != ImportInsuranceStates.Contracted && item.state != ImportInsuranceStates.ContractedByClient
  const showClientDisableButton = item.state != ImportInsuranceStates.Contracted && item.state != ImportInsuranceStates.ContractedByClient &&
    item.state != ImportInsuranceStates.Error
  const showSendAccessButton = item.state != ImportInsuranceStates.Contracted && item.state != ImportInsuranceStates.ContractedByClient &&
    item.state != ImportInsuranceStates.Error && !item.clientDisabled
  const showPublicLinkButton = item.state != ImportInsuranceStates.Error && !item.clientDisabled

  const insurer: IOfferCompany | undefined = item.insurerId ? { id: item.insurerId, name: item.insurerFullName! } : undefined

  const insureeName = formatInsuree(item.policyHolder)
  const subject = `${vehicleItem?.vehicle?.znacka ?? ''} ${vehicleItem?.vehicle?.model ?? ''}`.trim()

  const navigateTarget = useGetInsuranceParamsNavigationTarget(item, 4)

  const getStepsColor = useCallback(() => {
    switch (item.state) {
      case ImportInsuranceStates.Error: return 'red'
      case ImportInsuranceStates.Contracted: return 'green.400'
      case ImportInsuranceStates.ClientDisabled: return 'gray.300'
      default: return undefined
    }
  }, [item.state])

  return (
    <Tr onMouseEnter={setButtonsVisible.on} onMouseLeave={setButtonsVisible.off}>
      <Td
        display={allowSelect ? 'table-cell' : 'none'}
        onClick={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      >
        <Checkbox isChecked={isSelected}
          onChange={(e) => {
            onRowSelect(id)
          }} />
      </Td>

      <TableData textDecoration={item.contractCanceled ? 'line-through' : 'none'}>
        <Flex sx={{ columnGap: 4, rowGap: 1, flexWrap: 'wrap' }}>
          <Tooltip label={item.note} hasArrow placement="top" shouldWrapChildren>
            <Button size='lg' as={Link} to={navigateTarget} variant='link' target='_blank'>
              {id}
            </Button>
          </Tooltip>

          {item.exported &&
            <Tooltip label={'Exportováno'} hasArrow placement="top" shouldWrapChildren>
              <Badge colorScheme={'green'}><Text sx={{ fontWeight: 'bold' }}>E</Text></Badge>
            </Tooltip>
          }
        </Flex>
      </TableData>

      <TableData textDecoration={item.contractCanceled ? 'line-through' : 'none'}>
        <Text sx={{
          fontWeight: 'bold',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {item.importName}
        </Text>
        <Text sx={{ fontSize: 'sm' }}>{item.leasingNumber}</Text>
      </TableData>

      <TableData textDecoration={item.contractCanceled ? 'line-through' : 'none'}>

        {item.dateCreated &&
          <Text sx={{ fontSize: 'sm' }}>
            {formatDate(item.dateCreated, 'D.M.YYYY')}
          </Text>
        }

        {item.lastChange && (
          <Text sx={{ fontSize: 'sm', fontWeight: 'semibold' }}>
            {formatDate(item.lastChange, 'D.M.YYYY')}
          </Text>
        )}
      </TableData>

      <TableData sx={{ px: 0 }}>
        <VehicleLogo druh={vehicleItem?.vehicle?.druh} znacka={vehicleItem?.vehicle?.znacka} mx='auto' />
      </TableData>

      <TableData sx={{ pl: 4 }}
        textDecoration={item.contractCanceled ? 'line-through' : 'none'}>
        {insureeName &&
          <Text sx={{ fontSize: 'sm' }}>
            {insureeName}
          </Text>
        }

        {!!subject &&
          <Text
            sx={{
              fontWeight: 'bold',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subject}
          </Text>
        }
      </TableData>

      <TableData>
        {insurer &&
          <CompanyLogo
            insurer={insurer} offerType={item.offerType} minW='64px' width="64px" maxH="48px" />
        }
      </TableData>

      <TableData
        textDecoration={item.contractCanceled ? 'line-through' : 'none'}>
        <Flex sx={{ columnGap: 4, rowGap: 1, flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'center' }}>
          <Box>
            <Text
              sx={{
                fontWeight: 'bold',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.stateText}
            </Text>

            <StepsProgress
              progress={(item.step ?? 1)}
              passedColor={getStepsColor()}
            />
          </Box>
          <Box>
            {(item.clientDisabled ?? false) &&
              <Text>{item.clientDisableReason}</Text>
            }
            {!(item.clientDisabled ?? false) && item.lastError &&
              <Text sx={{ textWrap: 'wrap' }}>{item.lastError}</Text>
            }
          </Box>
        </Flex>
      </TableData>

      <TableData>

        {!buttonsVisible &&
          <Button variant='ghost' size='lg'>...</Button>
        }

        {buttonsVisible &&
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <Button variant="link" size="md">
                Více
                <ChevronDownIcon sx={{ w: '1.125rem', h: '1.125rem' }} />
              </Button>
            </PopoverTrigger>

            <PopoverContent>
              <PopoverArrow />
              <PopoverBody sx={{ p: 0 }} backgroundColor='gray.100'>
                <Grid sx={{ gap: 1, p: 0 }}>
                  <Button variant='ghost' as={Link} to={navigateTarget}>Upravit</Button>
                  {showSendAccessButton &&
                    <Button variant="ghost" onClick={() => { onSendAccess(item) }}>Poslat přístup</Button>
                  }
                  {showPublicLinkButton &&
                    <Button variant="ghost" onClick={() => { onDisplayPublicUrl(item) }}>Veřejný odkaz</Button>
                  }
                  {showClientDisableButton &&
                    <Button variant="ghost" onClick={() => { onToggleClientDisabled(item) }}>{item.clientDisabled ? 'Odblokovat' : 'Zablokovat'}</Button>
                  }
                  {showEditModal &&
                    <Button variant="ghost" onClick={() => { onOpenModal() }}>Doplnit údaje</Button>
                  }
                  {showDeleteButton &&
                    <Button variant="ghost" onClick={() => { onDeleteRow(id) }}>Smazat</Button>
                  }
                </Grid>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        }

        <EditInsuranceParamsModal
          isOpen={isOpenModal}
          onClose={onCloseModal}
          onSaved={() => reload()}
          insuranceParamsId={id}
        />
      </TableData>
    </Tr>
  )
}
